import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "./AdminLayout";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import api from "../../lib/api";
import { toast } from "sonner";
import { ShieldCheck, Mail, Network, ChevronRight, RefreshCw, AlertCircle, CheckCircle2 } from "lucide-react";

/**
 * /admin/integrations — single landing page for back-office wiring.
 *
 * Cards stack vertically. Each shows masked config + a green/amber/red status
 * pill so the on-call sees at a glance what's wired. Click → drill into the
 * full settings panel (existing KYCAID / Email / XGate pages).
 */
const ICONS = {
  kycaid: ShieldCheck,
  email:  Mail,
  xgate:  Network,
};

const TONE = {
  green: "bg-emerald-50 text-emerald-700 border-emerald-200",
  amber: "bg-amber-50 text-amber-700 border-amber-200",
  red:   "bg-rose-50 text-rose-700 border-rose-200",
};

export default function AdminIntegrations() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/integrations/status");
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load integrations status");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading || !data) {
    return <AdminLayout title="Integrations"><div className="text-sm text-muted-foreground">Loading…</div></AdminLayout>;
  }

  const OverallIcon = data.overall.level === "green" ? CheckCircle2 : AlertCircle;

  return (
    <AdminLayout
      title="Integrations"
      subtitle="Single landing for back-office wiring. All three rotate without a backend restart."
      actions={
        <Button onClick={load} variant="outline" size="sm" className="rounded-xl gap-1.5"
                data-testid="integrations-refresh-btn">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </Button>
      }
    >
      {/* Roll-up banner */}
      <div className={`rounded-2xl border p-4 mb-5 flex items-center gap-3 ${TONE[data.overall.level]}`}
           data-testid="integrations-overall-banner">
        <OverallIcon className="w-5 h-5 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="font-display font-bold">{data.overall.headline}</div>
          <div className="text-xs opacity-80 mt-0.5">
            {data.integrations.filter((i) => i.health.level === "green").length}/{data.integrations.length} integrations wired.
            Click any card to manage.
          </div>
        </div>
      </div>

      {/* Cards */}
      <div className="space-y-3" data-testid="integrations-cards">
        {data.integrations.map((it) => {
          const Icon = ICONS[it.id] || ShieldCheck;
          const tone = TONE[it.health.level];
          return (
            <button
              key={it.id}
              onClick={() => navigate(it.manage_path)}
              className="w-full xch-card p-4 text-left hover:shadow-md transition-shadow"
              data-testid={`integration-card-${it.id}`}
            >
              <div className="flex items-start gap-4">
                <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 border ${tone}`}>
                  <Icon className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-display font-bold text-foreground text-base">{it.label}</div>
                    <Badge variant="outline" className={`text-[10px] uppercase tracking-wider font-bold ${tone}`}
                           data-testid={`integration-${it.id}-status`}>
                      {it.health.headline}
                    </Badge>
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">{it.blurb}</div>
                  <div className="text-[11px] text-foreground mt-2">{it.health.detail}</div>
                  <IntegrationSummary integration={it} />
                </div>
                <ChevronRight className="w-5 h-5 text-muted-foreground shrink-0 self-center" />
              </div>
            </button>
          );
        })}
      </div>
    </AdminLayout>
  );
}

function IntegrationSummary({ integration: it }) {
  const c = it.config;
  if (it.id === "kycaid") {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-1 mt-3 text-[11px]">
        <Field label="Token"   value={c.api_token_set ? c.api_token_mask : "—"} source={c.source?.api_token} />
        <Field label="Form ID" value={c.form_id || "—"}                          source={c.source?.form_id} />
        <Field label="API base" value={c.api_base}                                source={c.source?.api_base} />
        <Field label="Wired"    value={c.wired ? "yes" : "no"} />
      </div>
    );
  }
  if (it.id === "email") {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-1 mt-3 text-[11px]">
        <Field label="Key"      value={c.api_key_set ? c.api_key_mask : "—"} source={c.source?.api_key} />
        <Field label="From"     value={c.sender_email}                       source={c.source?.sender_email} />
        <Field label="Ops"      value={c.ops_enabled ? "ON" : "off"}         source={c.source?.ops_enabled} />
        <Field label="Customer" value={c.customer_enabled ? "ON" : "off"}    source={c.source?.customer_enabled} />
      </div>
    );
  }
  if (it.id === "xgate") {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-1 mt-3 text-[11px]">
        <Field label="API base" value={c.api_base || "—"}                            source={c.source?.api_base} />
        <Field label="Token"    value={c.api_token_set ? c.api_token_mask : "—"}     source={c.source?.api_token} />
        <Field label="Webhook"  value={c.webhook_secret_set ? c.webhook_secret_mask : "—"} source={c.source?.webhook_secret} />
        <Field label="Checkout" value={c.checkout_base}                              source={c.source?.checkout_base} />
      </div>
    );
  }
  return null;
}

function Field({ label, value, source }) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-wider font-semibold text-muted-foreground">{label}</div>
      <div className="font-mono text-foreground truncate">{String(value ?? "—")}</div>
      {source && <div className="text-[9px] text-muted-foreground">({source})</div>}
    </div>
  );
}
